// Work's create dialogs (M2-07): a space (team, project or client; `spaces.create`), a sidebar
// group (the person's own, arrangement.ts), a board (name, template; `boards.create` makes its
// home channel and first views), a saved view (`views.create`) and a task with its properties
// (`tasks.create`, idempotent on `client_id`: a double submit makes one task). Each says what
// failed in the dialog and keeps what was typed.
import { ApiError, newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Segmented,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArchiveIcon,
  CopyIcon,
  EyeIcon,
  FileTextIcon,
  LinkIcon,
  LockIcon,
  PencilIcon,
  PlusIcon,
  Settings2Icon,
  SquareKanbanIcon,
  StarIcon,
  UsersIcon,
} from "lucide-react";
import { type FormEvent, type ReactElement, type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import { type CompanyAccess, isOperatorOnly } from "../company";
import { OperatorOnlyDisabled, OperatorOnlyMenuItem } from "../operator-only";
import { BoardArrangeItems } from "./board-controls";
import { DuplicateContainerDialog } from "./duplicate-dialogs";
import { itemTemplatesQuery, templateSummary } from "./item-templates";
import { dueInstant, PRIORITIES, type SeatPeople, startInstant } from "./model";
import { useTaskWrites } from "./task-writes";
import type { Board, View } from "./view-engine";
import { invalidateWork, workKeys } from "./work-queries";

const problemText = (error: unknown, fallback: string) =>
  error instanceof ApiError
    ? (error.problem?.detail ?? error.problem?.title ?? fallback)
    : error
      ? fallback
      : null;

export function NewSpaceDialog({
  api,
  company,
  trigger,
}: {
  api: Api;
  company: CompanyAccess;
  trigger: ReactElement;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"team" | "project" | "client">("project");
  const [description, setDescription] = useState("");
  const ids = { name: useId(), kind: useId(), description: useId() };
  const create = useMutation({
    mutationFn: () =>
      api.client.spaces.create({
        params: { co: company.id },
        idempotencyKey: newIdempotencyKey(),
        body: {
          name: name.trim(),
          kind,
          ...(description.trim() ? { description: description.trim() } : {}),
        },
      }),
    onSuccess: async (space) => {
      toast.add({
        title: `${space.name} created`,
        description: "With its channel.",
        type: "success",
      });
      // Not awaited: every active Work list refetching (Home's board summary on a company with
      // hundreds of boards) held the dialog open for seconds before the new space opened.
      // The space's page finds it in the cached list at once: until the refetch lands (seconds
      // behind the other Work lists on a busy company) it read "This space is not available".
      queryClient.setQueryData<SpaceList>(workKeys.spaces(company.id), (old) =>
        old && !old.data.some((s) => s.id === space.id)
          ? { ...old, data: [...old.data, space] }
          : old,
      );
      void invalidateWork(queryClient, company.id);
      setOpen(false);
      setName("");
      setDescription("");
      void navigate({
        to: "/c/$slug/work/s/$spaceId",
        params: { slug: company.slug, spaceId: space.id },
      });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  const error = problemText(create.error, "The space could not be created.");
  // OPERATOR-ONLY-UX: a space is created by a person member (it makes a channel); the trigger
  // stays visible, disabled, with the reason.
  if (isOperatorOnly(company)) return <OperatorOnlyDisabled>{trigger}</OperatorOnlyDisabled>;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={trigger} />
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>New space</DialogTitle>
            <DialogDescription>
              A space holds boards, channels and pages together.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
              <Input
                id={ids.name}
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.kind}>Kind</FieldLabel>
              <NativeSelect
                id={ids.kind}
                value={kind}
                onChange={(e) => setKind(e.target.value as typeof kind)}
              >
                <NativeSelectOption value="project">Project</NativeSelectOption>
                <NativeSelectOption value="team">Team</NativeSelectOption>
                <NativeSelectOption value="client">Client</NativeSelectOption>
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.description}>Description</FieldLabel>
              <Textarea
                id={ids.description}
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
            {error ? <FieldError>{error}</FieldError> : null}
          </FieldGroup>
          <DialogFooter>
            <Button type="submit" disabled={create.isPending || !name.trim()}>
              Create space
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function NewGroupDialog({
  onCreate,
  trigger,
}: {
  onCreate: (name: string, id: string) => void;
  trigger: ReactElement;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const id = useId();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={trigger} />
      <DialogContent className="sm:max-w-sm">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            onCreate(name.trim(), `g:${Date.now().toString(36)}`);
            setName("");
            setOpen(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>New group</DialogTitle>
            <DialogDescription>
              A folder of spaces in your sidebar. Drag spaces into it; only your sidebar changes.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={id}>Group name</FieldLabel>
            <Input id={id} required value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <DialogFooter>
            <Button type="submit" disabled={!name.trim()}>
              Create group
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** wp/ACTIONS: rename a sidebar group (the person's own arrangement; nobody else sees it). */
export function RenameGroupDialog({
  name: initial,
  onSave,
  onClose,
}: {
  name: string;
  onSave: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(initial);
  const id = useId();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) onSave(name.trim());
            onClose();
          }}
        >
          <DialogHeader>
            <DialogTitle>Rename group</DialogTitle>
            <DialogDescription>Only your sidebar changes.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={id}>Group name</FieldLabel>
            <Input id={id} required value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <DialogFooter>
            <Button type="submit" disabled={!name.trim() || name.trim() === initial}>
              Rename
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type SpaceList = Awaited<ReturnType<Api["client"]["spaces"]["list"]>>;
type SpaceRow = SpaceList["data"][number];

/**
 * Space settings (MEMBER-UX; ClickUp's space settings): rename, privacy and "Members can: View /
 * Edit" (`settings.member_access`), saved with `spaces.update`. Owners and admins open it from a
 * space's "…" menu in the Work sidebar. "Members can" is a shared space's: a private space is its
 * invited people's, a team space its team's, so the switch is not offered there. The other
 * settings keys are kept as they are.
 */
export function SpaceSettingsDialog({
  api,
  company,
  space,
  boards = [],
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  space: SpaceRow;
  /** BOARD-DEFAULT-VIEW: the space's live boards in order, for "Opens on". */
  boards?: readonly { id: string; name: string }[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(space.name);
  // BOARD-DEFAULT-VIEW: what opening the space shows ("" = its first board).
  const [opensOn, setOpensOn] = useState<string>(space.settings.opens_on ?? "");
  const [privacy, setPrivacy] = useState<"shared" | "private">(
    space.is_private ? "private" : "shared",
  );
  const [access, setAccess] = useState<"viewer" | "editor">(
    space.settings.member_access === "editor" ? "editor" : "viewer",
  );
  const ids = { name: useId(), opensOn: useId() };
  const team = space.kind === "team";
  const shared = !team && privacy === "shared";
  const opensChanged = (opensOn || null) !== (space.settings.opens_on ?? null);
  const save = useMutation({
    mutationFn: () =>
      api.client.spaces.update({
        params: { co: company.id, id: space.id },
        body: {
          ...(name.trim() !== space.name ? { name: name.trim() } : {}),
          ...(!team && (privacy === "private") !== space.is_private
            ? { is_private: privacy === "private" }
            : {}),
          ...(shared || opensChanged
            ? {
                settings: {
                  ...space.settings,
                  ...(shared ? { member_access: access } : {}),
                  opens_on: opensOn || null,
                },
              }
            : {}),
        },
      }),
    onSuccess: async (s) => {
      toast.add({ title: `${s.name} saved`, type: "success" });
      await invalidateWork(queryClient, company.id);
      onClose();
    },
  });
  const error = problemText(save.error, "The space settings were not saved.");
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="sm:max-w-md" data-slot="space-settings">
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Space settings</DialogTitle>
            <DialogDescription>
              {space.name}: who can see it and who can change it.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
              <Input
                id={ids.name}
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.opensOn}>Opens on</FieldLabel>
              <NativeSelect
                id={ids.opensOn}
                value={opensOn}
                onChange={(e) => setOpensOn(e.target.value)}
              >
                <NativeSelectOption value="">First board</NativeSelectOption>
                {boards.map((b) => (
                  <NativeSelectOption key={b.id} value={b.id}>
                    {b.name}
                  </NativeSelectOption>
                ))}
                <NativeSelectOption value="overview">Overview</NativeSelectOption>
              </NativeSelect>
            </Field>
            {team ? (
              <FieldDescription>
                A team's space: the team's people edit it and its leads see it. Change who is on the
                team under Setup › Teams.
              </FieldDescription>
            ) : (
              <>
                <Field>
                  <span className="text-sm font-medium text-foreground">Privacy</span>
                  <Segmented
                    aria-label="Privacy"
                    value={privacy}
                    onValueChange={setPrivacy}
                    options={[
                      { value: "shared", label: "Shared", icon: UsersIcon },
                      { value: "private", label: "Private", icon: LockIcon },
                    ]}
                  />
                  <FieldDescription>
                    {privacy === "shared"
                      ? "Everyone in the company sees it."
                      : "Only the people added to it, and owners and admins, see it."}
                  </FieldDescription>
                </Field>
                {shared ? (
                  <Field>
                    <span className="text-sm font-medium text-foreground">Members can</span>
                    <Segmented
                      aria-label="Members can"
                      value={access}
                      onValueChange={setAccess}
                      options={[
                        { value: "viewer", label: "View", icon: EyeIcon },
                        { value: "editor", label: "Edit", icon: PencilIcon },
                      ]}
                    />
                    <FieldDescription>
                      {access === "editor"
                        ? "Members create and edit tasks on its boards."
                        : "Members read and comment; only owners, admins and the space's editors change tasks."}
                    </FieldDescription>
                  </Field>
                ) : null}
              </>
            )}
            {error ? <FieldError>{error}</FieldError> : null}
          </FieldGroup>
          <DialogFooter>
            <Button variant="secondary" type="button" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !name.trim()}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The spec's board templates (06 "Creating a board: name, template …"), with what each gives. */
export const BOARD_TEMPLATES = [
  { value: "kanban", label: "Kanban", hint: "Backlog → Done columns; Board view first" },
  { value: "blank", label: "Blank", hint: "To do / Doing / Done; List view first" },
  { value: "sprint", label: "Sprint", hint: "Points and a Sprint field; board and burndown list" },
  { value: "content_calendar", label: "Content calendar", hint: "Channel field; Calendar first" },
  { value: "client_onboarding", label: "Client onboarding", hint: "The onboarding checklist" },
  {
    value: "ideas",
    label: "Ideas",
    hint: "Proposed → Approved, Rejected or Used; DEs file ideas here for you to vet",
  },
  {
    value: "content_pipeline",
    label: "Content pipeline",
    hint: "Research → Ideas → Review → Published; you approve ideas and finished pieces",
  },
] as const;

export type SpaceOption = { id: string; name: string; archived_at: string | null };

/**
 * New board: name and template; the space is fixed (a space's header or row) or picked from
 * `spaces` (Work home's "+ New board"). Opens the board when made.
 */
export function NewBoardDialog({
  api,
  company,
  spaceId: fixed,
  spaces = [],
  trigger,
  open: openProp,
  onOpenChange,
}: {
  api: Api;
  company: CompanyAccess;
  spaceId?: string | undefined;
  spaces?: SpaceOption[];
  trigger?: ReactElement | undefined;
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [ownOpen, setOwnOpen] = useState(false);
  const open = openProp ?? ownOpen;
  const setOpen = (o: boolean) => {
    setOwnOpen(o);
    onOpenChange?.(o);
  };
  const [name, setName] = useState("");
  const live = spaces.filter((s) => !s.archived_at);
  const [spaceId, setSpaceId] = useState(fixed ?? live[0]?.id ?? "");
  const [template, setTemplate] = useState<(typeof BOARD_TEMPLATES)[number]["value"]>("kanban");
  const ids = { name: useId(), space: useId(), template: useId() };
  const create = useMutation({
    mutationFn: () =>
      api.client.boards.create({
        params: { co: company.id },
        idempotencyKey: newIdempotencyKey(),
        body: { space_id: fixed ?? spaceId, name: name.trim(), template },
      }),
    onSuccess: async (board) => {
      toast.add({
        title: `${board.name} created`,
        description: "With its home channel.",
        type: "success",
      });
      // Not awaited (as the space): the new board opens without waiting for every Work list.
      void invalidateWork(queryClient, company.id);
      setOpen(false);
      setName("");
      void navigate({
        to: "/c/$slug/work/b/$boardId",
        params: { slug: company.slug, boardId: board.id },
      });
    },
  });
  const error = problemText(create.error, "The board could not be created.");
  const chosen = BOARD_TEMPLATES.find((t) => t.value === template);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger ? <DialogTrigger render={trigger} /> : null}
      <DialogContent className="sm:max-w-md">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>New board</DialogTitle>
            <DialogDescription>
              Tasks, statuses and views, with a home channel for its digests.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
              <Input
                id={ids.name}
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            {!fixed ? (
              <Field>
                <FieldLabel htmlFor={ids.space}>Space</FieldLabel>
                <NativeSelect
                  id={ids.space}
                  value={spaceId}
                  onChange={(e) => setSpaceId(e.target.value)}
                >
                  {live.map((s) => (
                    <NativeSelectOption key={s.id} value={s.id}>
                      {s.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                {!live.length ? <FieldDescription>No space yet.</FieldDescription> : null}
              </Field>
            ) : null}
            <Field>
              <FieldLabel htmlFor={ids.template}>Template</FieldLabel>
              <NativeSelect
                id={ids.template}
                value={template}
                onChange={(e) => setTemplate(e.target.value as typeof template)}
              >
                {BOARD_TEMPLATES.map((t) => (
                  <NativeSelectOption key={t.value} value={t.value}>
                    {t.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <FieldDescription>
                {chosen?.hint}. Statuses follow the space's defaults when it has any.
              </FieldDescription>
            </Field>
            {error ? <FieldError>{error}</FieldError> : null}
          </FieldGroup>
          <DialogFooter>
            <Button
              type="submit"
              disabled={create.isPending || !name.trim() || !(fixed ?? spaceId)}
            >
              Create board
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// WORK-OS: rename and archive, for the sidebar rows and the board header -------------------------

/** One name field; `onSave` resolves when the server took it (the dialog closes then). */
export function RenameDialog({
  what,
  name: initial,
  open,
  onOpenChange,
  onSave,
}: {
  /** wp/ACTIONS: a saved view and a custom field rename here too. */
  what: "board" | "space" | "view" | "field";
  name: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string) => Promise<unknown>;
}) {
  const [name, setName] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const id = useId();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <form
          className="grid gap-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setPending(true);
            setError(null);
            try {
              await onSave(name.trim());
              onOpenChange(false);
            } catch (err) {
              setError(problemText(err, `The ${what} could not be renamed.`));
            } finally {
              setPending(false);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Rename {what}</DialogTitle>
            <DialogDescription>Everyone sees the new name.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={id}>Name</FieldLabel>
            <Input id={id} required value={name} onChange={(e) => setName(e.target.value)} />
            {error ? <FieldError>{error}</FieldError> : null}
          </Field>
          <DialogFooter>
            <Button type="submit" disabled={pending || !name.trim() || name.trim() === initial}>
              Rename
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** "Archive X?" with what archiving keeps (nothing is deleted, 06 §Spaces). */
export function ArchiveDialog({
  what,
  name,
  open,
  onOpenChange,
  onConfirm,
}: {
  what: "board" | "space";
  name: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive {name}?</AlertDialogTitle>
          <AlertDialogDescription>
            {what === "board"
              ? "Its tasks and home channel go read-only and leave the sidebar. Nothing is deleted."
              : "Its boards, channels and docs are archived with it. Nothing is deleted."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={onConfirm}>
            Archive
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * A board's "…" menu (sidebar row and board header): Open, New task, Copy link, Rename and
 * Archive; `starred` adds the favorite toggle. Rename and Archive are the two dialogs above.
 * BOARD-ORDER: a whole board row adds Move up / down, Move to space, Colour and Lead.
 * wp/ACTIONS: `icons` renders visible hover icons before the trigger that open the same Rename
 * and Archive dialogs (the menu stays the second path).
 */
export function BoardMenu({
  api,
  company,
  board,
  trigger,
  onNewTask,
  star,
  icons,
  onSettings,
  align = "end",
}: {
  api: Api;
  company: CompanyAccess;
  board: Pick<Board, "id" | "name" | "version" | "archived_at"> &
    Partial<Pick<Board, "space_id" | "position" | "color" | "lead_seat_id">>;
  trigger: ReactElement;
  onNewTask?: (() => void) | undefined;
  star?: { on: boolean; toggle: () => void } | undefined;
  icons?: ((open: ContainerIconActions) => ReactNode) | undefined;
  /** wp/ACTIONS: "Board settings…" (the board header's sheet). */
  onSettings?: (() => void) | undefined;
  align?: "start" | "end";
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [renaming, setRenaming] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const co = company.id;
  const rename = (name: string) =>
    api.client.boards
      .update({ params: { co, id: board.id }, ifMatch: board.version, body: { name } })
      .then(async () => {
        toast.add({ title: `Renamed to ${name}`, type: "success" });
        await invalidateWork(queryClient, co);
      });
  const archive = useMutation({
    mutationFn: () =>
      api.client.boards.archive({
        params: { co, id: board.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: async () => {
      toast.add({ title: `${board.name} archived`, type: "success" });
      await invalidateWork(queryClient, co);
      void navigate({ to: "/c/$slug/work/boards", params: { slug: company.slug } });
    },
  });
  const copyLink = () =>
    void navigator.clipboard
      ?.writeText(`${window.location.origin}/c/${company.slug}/work/b/${board.id}`)
      .then(
        () => toast.add({ title: "Link copied" }),
        () => toast.add({ title: "The link could not be copied", type: "warning" }),
      );
  return (
    <>
      {icons && !board.archived_at
        ? icons({ rename: () => setRenaming(true), archive: () => setArchiving(true) })
        : null}
      <DropdownMenu>
        <DropdownMenuTrigger render={trigger} />
        <DropdownMenuContent align={align} className="min-w-44">
          <DropdownMenuItem
            render={
              <Link
                to="/c/$slug/work/b/$boardId"
                params={{ slug: company.slug, boardId: board.id }}
              />
            }
          >
            <SquareKanbanIcon aria-hidden="true" />
            Open board
          </DropdownMenuItem>
          {onNewTask && !board.archived_at ? (
            <DropdownMenuItem onClick={onNewTask}>
              <PlusIcon aria-hidden="true" />
              New task
            </DropdownMenuItem>
          ) : null}
          {star ? (
            <DropdownMenuItem onClick={star.toggle}>
              <StarIcon aria-hidden="true" className={star.on ? "fill-current text-warning" : ""} />
              {star.on ? "Unstar" : "Star"}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onClick={copyLink}>
            <LinkIcon aria-hidden="true" />
            Copy link
          </DropdownMenuItem>
          {onSettings ? (
            <DropdownMenuItem onClick={onSettings}>
              <Settings2Icon aria-hidden="true" />
              Board settings…
            </DropdownMenuItem>
          ) : null}
          {/* BOARD-ORDER: Move up / down, Move to space, Colour and Lead. */}
          {!board.archived_at && board.space_id !== undefined ? (
            <BoardArrangeItems api={api} company={company} board={board as Board} />
          ) : null}
          {!board.archived_at ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setRenaming(true)}>
                <PencilIcon aria-hidden="true" />
                Rename
              </DropdownMenuItem>
              {/* WOS-BUNDLE */}
              <DropdownMenuItem onClick={() => setDuplicating(true)}>
                <CopyIcon aria-hidden="true" />
                Duplicate…
              </DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onClick={() => setArchiving(true)}>
                <ArchiveIcon aria-hidden="true" />
                Archive
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {duplicating ? (
        <DuplicateContainerDialog
          api={api}
          company={company}
          kind="board"
          container={board}
          open={duplicating}
          onOpenChange={setDuplicating}
        />
      ) : null}
      {renaming ? (
        <RenameDialog
          what="board"
          name={board.name}
          open={renaming}
          onOpenChange={setRenaming}
          onSave={rename}
        />
      ) : null}
      <ArchiveDialog
        what="board"
        name={board.name}
        open={archiving}
        onOpenChange={setArchiving}
        onConfirm={() => archive.mutate()}
      />
    </>
  );
}

/** What a container menu's `icons` open: the menu's own Rename and Archive dialogs. */
export type ContainerIconActions = { rename: () => void; archive: () => void };

/** A space's "…" menu: New board, New doc, Rename and Archive (the owner's and editors' writes). */
export function SpaceMenu({
  api,
  company,
  space,
  trigger,
  onNewBoard,
  onNewDoc,
  onSettings,
  star,
  icons,
  align = "end",
}: {
  api: Api;
  company: CompanyAccess;
  space: { id: string; name: string; archived_at: string | null };
  trigger: ReactElement;
  onNewBoard?: (() => void) | undefined;
  onNewDoc?: (() => void) | undefined;
  /** MEMBER-UX: owners and admins (the sidebar passes it for them, not for a personal space). */
  onSettings?: (() => void) | undefined;
  star?: { on: boolean; toggle: () => void } | undefined;
  /** wp/ACTIONS: visible hover icons opening Rename / Archive (as `BoardMenu`'s). */
  icons?: ((open: ContainerIconActions) => ReactNode) | undefined;
  align?: "start" | "end";
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [renaming, setRenaming] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const co = company.id;
  const rename = (name: string) =>
    api.client.spaces.update({ params: { co, id: space.id }, body: { name } }).then(async () => {
      toast.add({ title: `Renamed to ${name}`, type: "success" });
      await invalidateWork(queryClient, co);
    });
  const archive = useMutation({
    mutationFn: () =>
      api.client.spaces.archive({
        params: { co, id: space.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: async () => {
      toast.add({ title: `${space.name} archived`, type: "success" });
      await invalidateWork(queryClient, co);
      void navigate({ to: "/c/$slug/work/boards", params: { slug: company.slug } });
    },
  });
  return (
    <>
      {icons && !space.archived_at
        ? icons({ rename: () => setRenaming(true), archive: () => setArchiving(true) })
        : null}
      <DropdownMenu>
        <DropdownMenuTrigger render={trigger} />
        <DropdownMenuContent align={align} className="min-w-44">
          {onNewBoard && !space.archived_at ? (
            <DropdownMenuItem onClick={onNewBoard}>
              <SquareKanbanIcon aria-hidden="true" />
              New board
            </DropdownMenuItem>
          ) : null}
          {onNewDoc && !space.archived_at ? (
            <DropdownMenuItem onClick={onNewDoc}>
              <FileTextIcon aria-hidden="true" />
              New doc
            </DropdownMenuItem>
          ) : null}
          {star ? (
            <DropdownMenuItem onClick={star.toggle}>
              <StarIcon aria-hidden="true" className={star.on ? "fill-current text-warning" : ""} />
              {star.on ? "Unstar" : "Star"}
            </DropdownMenuItem>
          ) : null}
          {!space.archived_at ? (
            <>
              <DropdownMenuSeparator />
              {onSettings ? (
                <DropdownMenuItem onClick={onSettings}>
                  <Settings2Icon aria-hidden="true" />
                  Space settings
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onClick={() => setRenaming(true)}>
                <PencilIcon aria-hidden="true" />
                Rename
              </DropdownMenuItem>
              {/* WOS-BUNDLE. OPERATOR-ONLY-UX: a duplicate is a new space, which only a person
                  member creates; shown disabled with the reason. */}
              {isOperatorOnly(company) ? (
                <OperatorOnlyMenuItem icon={<CopyIcon aria-hidden="true" />} label="Duplicate…" />
              ) : (
                <DropdownMenuItem onClick={() => setDuplicating(true)}>
                  <CopyIcon aria-hidden="true" />
                  Duplicate…
                </DropdownMenuItem>
              )}
              <DropdownMenuItem variant="destructive" onClick={() => setArchiving(true)}>
                <ArchiveIcon aria-hidden="true" />
                Archive
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {duplicating ? (
        <DuplicateContainerDialog
          api={api}
          company={company}
          kind="space"
          container={space}
          open={duplicating}
          onOpenChange={setDuplicating}
        />
      ) : null}
      {renaming ? (
        <RenameDialog
          what="space"
          name={space.name}
          open={renaming}
          onOpenChange={setRenaming}
          onSave={rename}
        />
      ) : null}
      <ArchiveDialog
        what="space"
        name={space.name}
        open={archiving}
        onOpenChange={setArchiving}
        onConfirm={() => archive.mutate()}
      />
    </>
  );
}

export const VIEW_KINDS: { value: View["kind"]; label: string }[] = [
  { value: "board", label: "Board" },
  { value: "list", label: "List" },
  { value: "table", label: "Table" },
  { value: "calendar", label: "Calendar" },
  { value: "timeline", label: "Timeline" },
  { value: "gallery", label: "Gallery" },
];

/** "Save as new view": a name and a kind; the current filter, sort and grouping go with it. */
export function NewViewDialog({
  open,
  onOpenChange,
  initialKind,
  onCreate,
  pending,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialKind: View["kind"];
  onCreate: (name: string, kind: View["kind"]) => void;
  pending: boolean;
  error: unknown;
}) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<View["kind"]>(initialKind);
  const ids = { name: useId(), kind: useId() };
  const text = problemText(error, "The view could not be saved.");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            onCreate(name.trim(), kind);
          }}
        >
          <DialogHeader>
            <DialogTitle>Save as a new view</DialogTitle>
            <DialogDescription>Everyone on this board sees it as a tab.</DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={ids.name}>View name</FieldLabel>
              <Input
                id={ids.name}
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.kind}>Show as</FieldLabel>
              <NativeSelect
                id={ids.kind}
                value={kind}
                onChange={(e) => setKind(e.target.value as View["kind"])}
              >
                {VIEW_KINDS.map((k) => (
                  <NativeSelectOption key={k.value} value={k.value}>
                    {k.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            {text ? <FieldError>{text}</FieldError> : null}
          </FieldGroup>
          <DialogFooter>
            <Button type="submit" disabled={pending || !name.trim()}>
              Save for everyone
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export type NewTaskDefaults = {
  status?: string;
  priority?: number;
  due?: string;
  /** T4-VIEWS-MEDIA: a calendar running on the start date or a date field adds on that date. */
  start?: string;
  fields?: Record<string, unknown>;
  parentTaskId?: string;
  /** SEAT-DRAWER-MODALS: the seat the task is for (the assignee, or the delegate for a DE's seat). */
  assignee?: string;
};

/** "Add task" with its properties: assignee (a person's seat) and delegate (a DE's seat). */
export function NewTaskDialog({
  api,
  company,
  board: fixed,
  boards = [],
  people,
  open,
  onOpenChange,
  defaults,
  onCreated,
}: {
  api: Api;
  company: CompanyAccess;
  /** The board the task goes on; without one, `boards` is the picker (My tasks, a space). */
  board?: Board | undefined;
  boards?: Board[];
  people: SeatPeople;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaults?: NewTaskDefaults;
  onCreated?: (id: string) => void;
}) {
  const [boardId, setBoardId] = useState(fixed?.id ?? boards[0]?.id ?? "");
  const board: Board | undefined = fixed ?? boards.find((b) => b.id === boardId);
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState(defaults?.status ?? board?.status_set[0]?.key ?? "");
  const [priority, setPriority] = useState(String(defaults?.priority ?? 0));
  const forDe = defaults?.assignee ? people.of(defaults.assignee).de : false;
  const [owner, setOwner] = useState(defaults?.assignee && !forDe ? defaults.assignee : "");
  const [delegate, setDelegate] = useState(defaults?.assignee && forDe ? defaults.assignee : "");
  const [start, setStart] = useState(defaults?.start ?? "");
  const [due, setDue] = useState(defaults?.due ?? "");
  const [body, setBody] = useState("");
  const [clientId, setClientId] = useState(() => newIdempotencyKey());
  // B4-ITEM-TEMPLATES: the board's item templates; picking one fills the form, the server the rest
  const templates = useQuery({
    ...itemTemplatesQuery(api, company.id, board?.id ?? ""),
    enabled: Boolean(board) && open,
  });
  const templateList = (templates.data?.data ?? []).filter((t) => t.board_id === board?.id);
  const [templateId, setTemplateId] = useState("");
  const template = templateList.find((t) => t.id === templateId);
  // a template's assignees past the one person and one DE the form shows (sent along with them)
  const [moreSeats, setMoreSeats] = useState<string[]>([]);
  const pickTemplate = (id: string) => {
    setTemplateId(id);
    const t = templateList.find((x) => x.id === id);
    // each pick starts from the dialog's defaults, so nothing of a previous template stays
    const first = defaults?.status ?? board?.status_set[0]?.key ?? "";
    const p = t?.payload;
    setStatus(p?.status && board?.status_set.some((s) => s.key === p.status) ? p.status : first);
    setPriority(String(p?.priority ?? defaults?.priority ?? 0));
    if (p) setTitle(p.title);
    const seats = p?.assignee_seat_ids ?? [];
    const person = seats.find((s) => !people.of(s).de);
    const de = seats.find((s) => people.of(s).de);
    const keep = defaults?.assignee;
    setOwner(person ?? (keep && !forDe ? keep : ""));
    setDelegate(de ?? (keep && forDe ? keep : ""));
    setMoreSeats(seats.filter((s) => s !== person && s !== de));
  };
  const ids = {
    template: useId(),
    title: useId(),
    board: useId(),
    status: useId(),
    priority: useId(),
    owner: useId(),
    delegate: useId(),
    start: useId(),
    due: useId(),
    body: useId(),
  };
  const humans = people.all.filter((p) => !p.de && !p.closed);
  const agents = people.all.filter((p) => p.de && !p.closed);
  // A status of the chosen board (the picker may have moved to another board since).
  const statusOn = board?.status_set.some((s) => s.key === status)
    ? status
    : (board?.status_set[0]?.key ?? "");
  // WORK-PERF: the task shows on its board at once and the dialog closes; a refusal takes the
  // row back out and the toast says why.
  const writes = useTaskWrites(api, company);
  const create = () => {
    writes.create(
      {
        board_id: board?.id ?? "",
        title: title.trim(),
        status: statusOn,
        priority: Number(priority),
        ...(defaults?.parentTaskId ? { parent_task_id: defaults.parentTaskId } : {}),
        assignee_seat_ids: [
          ...new Set([owner, delegate, ...(template ? moreSeats : [])].filter(Boolean)),
        ],
        ...(due ? { due_at: dueInstant(due) } : {}),
        ...(start ? { start_at: startInstant(start) } : {}),
        ...(defaults?.fields ? { fields: defaults.fields } : {}),
        ...(body.trim() ? { body_markdown: body.trim() } : {}),
        ...(template ? { template_id: template.id } : {}),
      },
      { clientId, ...(onCreated ? { onSuccess: (task) => onCreated(task.id) } : {}) },
    );
    setTitle("");
    setBody("");
    setTemplateId("");
    setMoreSeats([]);
    setClientId(newIdempotencyKey());
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            create();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {defaults?.parentTaskId
                ? "New subtask"
                : fixed
                  ? `New task in ${fixed.name}`
                  : "New task"}
            </DialogTitle>
            <DialogDescription>
              Assign a person's seat; add a digital employee as the delegate to have it do the work
              while the person stays accountable.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            {templateList.length ? (
              <Field>
                <FieldLabel htmlFor={ids.template} className="flex items-center gap-1">
                  Template
                  <InfoTip label="About templates">
                    Fills the form; its description, checklist, fields and tags come with it. What
                    you change here wins.
                  </InfoTip>
                </FieldLabel>
                <NativeSelect
                  id={ids.template}
                  value={templateId}
                  onChange={(e) => pickTemplate(e.target.value)}
                >
                  <NativeSelectOption value="">None</NativeSelectOption>
                  {templateList.map((t) => (
                    <NativeSelectOption key={t.id} value={t.id}>
                      {t.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                {template ? (
                  <FieldDescription data-slot="template-summary">
                    {[template.description, templateSummary(template)]
                      .filter(Boolean)
                      .join(" · ") || "Title, status and priority."}
                  </FieldDescription>
                ) : null}
              </Field>
            ) : null}
            <Field>
              <FieldLabel htmlFor={ids.title}>Title</FieldLabel>
              <Input
                id={ids.title}
                required
                maxLength={500}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Field>
            {!fixed ? (
              <Field>
                <FieldLabel htmlFor={ids.board}>Board</FieldLabel>
                <NativeSelect
                  id={ids.board}
                  value={boardId}
                  onChange={(e) => setBoardId(e.target.value)}
                >
                  {boards.map((b) => (
                    <NativeSelectOption key={b.id} value={b.id}>
                      {b.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                {!boards.length ? <FieldDescription>No board yet.</FieldDescription> : null}
              </Field>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor={ids.status}>Status</FieldLabel>
                <NativeSelect
                  id={ids.status}
                  value={statusOn}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {(board?.status_set ?? []).map((s) => (
                    <NativeSelectOption key={s.key} value={s.key}>
                      {s.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
              <Field>
                <FieldLabel htmlFor={ids.priority}>Priority</FieldLabel>
                <NativeSelect
                  id={ids.priority}
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  {PRIORITIES.map((p) => (
                    <NativeSelectOption key={p.value} value={String(p.value)}>
                      {p.label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
              <Field>
                <FieldLabel htmlFor={ids.owner}>Assignee</FieldLabel>
                <NativeSelect
                  id={ids.owner}
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                >
                  <NativeSelectOption value="">No one</NativeSelectOption>
                  {humans.map((p) => (
                    <NativeSelectOption key={p.seatId} value={p.seatId}>
                      {p.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
              <Field>
                <FieldLabel htmlFor={ids.delegate}>Delegate (digital employee)</FieldLabel>
                <NativeSelect
                  id={ids.delegate}
                  value={delegate}
                  onChange={(e) => setDelegate(e.target.value)}
                >
                  <NativeSelectOption value="">None</NativeSelectOption>
                  {agents.map((p) => (
                    <NativeSelectOption key={p.seatId} value={p.seatId}>
                      {p.name} · {p.hint}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
              <Field>
                <FieldLabel htmlFor={ids.start}>Start</FieldLabel>
                <Input
                  id={ids.start}
                  type="date"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor={ids.due}>Due</FieldLabel>
                <Input
                  id={ids.due}
                  type="date"
                  value={due}
                  onChange={(e) => setDue(e.target.value)}
                />
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor={ids.body}>Description</FieldLabel>
              <Textarea
                id={ids.body}
                rows={3}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
              <FieldDescription>
                {template?.payload.body?.length && !body.trim()
                  ? "Left empty: the template's description."
                  : "Becomes the task's page body."}
              </FieldDescription>
            </Field>
          </FieldGroup>
          <DialogFooter>
            <Button type="submit" disabled={!title.trim() || !board}>
              Create task
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
